/**
 * Styled-vs-unstyled checkers self-play — the strength-budget measurement P5
 * is accepted on ("styled persona scores within a tolerance of the unstyled
 * baseline of its band… measured over seeded games", spec §Playing styles;
 * ratified band 45–55%).
 *
 * Design notes, all learned from the Step-0 probe (2026-10-23):
 *
 * - **Games don't terminate naturally.** Equal-depth self-play shuffled to the
 *   300-ply cap in 20/20 probe games — the checkers adapter has no repetition
 *   or no-progress draw rule. So each game ends at `capPlies` and is
 *   **adjudicated by material**: |lead| ≥ 1 man (100) decides, otherwise a
 *   draw (scored 0.5). The adjudication is identical for both seats.
 * - **Seeded openings vary the games**: `openingPlies` random legal playout
 *   (seededRandom), then full play. Seat alternates by seed — a built-in
 *   mirror check (`bySeat`).
 * - The styled side re-ranks through the SAME `pickStyledMove` the app store
 *   uses, from the same MultiPV candidate set; the unstyled side takes
 *   `candidates[0]`, exactly today's behaviour. Divergence counts how often
 *   the style actually overrode the engine's first choice — a style that
 *   never diverges can't be measured (and "styles change moves" would be
 *   false), so the calibration test fails it.
 */
import { CheckersAdapter } from '../checkers.js';
import { CheckersAI } from '../engine.js';
import { seededRandom } from '../trick/rng.js';
import { pickStyledMove, STYLE_MULTI_PV, type StyleAxes } from './rank.js';

const SQUARES: readonly string[] = (() => {
  const files = 'abcdefgh';
  const out: string[] = [];
  for (let rank = 1; rank <= 8; rank += 1) for (const f of files) out.push(`${f}${rank}`);
  return out;
})();

function legalUcis(adapter: CheckersAdapter, pos: ReturnType<CheckersAdapter['newGame']>): string[] {
  const out: string[] = [];
  for (const from of SQUARES) {
    for (const t of adapter.legalMovesForSquare(pos, from)) out.push(`${from}${t.to}`);
  }
  return out.sort();
}

export interface SelfplayOptions {
  readonly style: StyleAxes;
  readonly games: number;
  /** First seed; games use seed, seed+1, … Default 1. */
  readonly seed?: number;
  /** Search depth for BOTH sides (1 = the fast CI concession, 2 = kitten). */
  readonly depth: number;
  readonly capPlies?: number;
  readonly openingPlies?: number;
  /** Window override (calibration ladder experiments; default = per-game). */
  readonly windowCp?: number;
}

export interface SeatScore {
  readonly games: number;
  readonly score: number;
}

export interface SelfplayResult {
  readonly games: number;
  /** Styled side's score: wins 1, draws 0.5, losses 0. */
  readonly scorePct: number;
  readonly draws: number;
  readonly divergent: number;
  readonly styledPlies: number;
  readonly bySeat: { readonly asWhite: SeatScore; readonly asBlack: SeatScore };
  readonly seedBase: number;
}

/** One game's outcome from the STYLED side's point of view. */
interface GameOutcome {
  readonly styledScore: number;
  readonly draw: boolean;
  readonly divergent: number;
  readonly styledPlies: number;
  readonly asWhite: boolean;
}

async function playGame(
  style: StyleAxes,
  seed: number,
  depth: number,
  capPlies: number,
  openingPlies: number,
  windowCp: number | undefined,
): Promise<GameOutcome> {
  const adapter = new CheckersAdapter();
  const engines = { w: new CheckersAI({ depth }), b: new CheckersAI({ depth }) };
  const random = seededRandom(seed);
  const styledSeat: 'w' | 'b' = seed % 2 === 0 ? 'w' : 'b';
  const asWhite = styledSeat === 'w';
  let pos = adapter.newGame();

  // Seeded random opening — this is where per-game variety comes from, since
  // engine play itself is fully deterministic.
  for (let i = 0; i < openingPlies && !adapter.result(pos); i += 1) {
    const ucis = legalUcis(adapter, pos);
    if (ucis.length === 0) break;
    const uci = ucis[Math.floor(random() * ucis.length)]!;
    pos = adapter.makeMove(pos, uci.slice(0, 2), uci.slice(2, 4));
  }

  let divergent = 0;
  let styledPlies = 0;
  let winner: 'w' | 'b' | null = null;
  let plies = 0;
  while (plies < capPlies && !adapter.result(pos)) {
    const engine = engines[pos.turn];
    await engine.setPosition(pos.fen);
    const candidates = await engine.getBestMoves(STYLE_MULTI_PV);
    const best = candidates[0];
    if (!best) break;
    let pick = best;
    if (pos.turn === styledSeat) {
      styledPlies += 1;
      try {
        pick = pickStyledMove(adapter, pos, candidates, style, windowCp);
      } catch {
        pick = best;
      }
      if (pick.uci !== best.uci) divergent += 1;
    }
    pos = adapter.makeMove(pos, pick.uci.slice(0, 2), pick.uci.slice(2, 4));
    plies += 1;
  }

  const natural = adapter.result(pos);
  if (natural && 'winner' in natural) winner = natural.winner;
  if (!winner && plies >= capPlies) {
    // Adjudication: a full man's lead decides, otherwise a draw.
    const material = adapter.evaluate(pos); // white-POV, man = 100
    if (material >= 100) winner = 'w';
    else if (material <= -100) winner = 'b';
  }
  const draw = winner == null;
  const styledScore = winner == null ? 0.5 : winner === styledSeat ? 1 : 0;
  return { styledScore, draw, divergent, styledPlies, asWhite };
}

/**
 * Run `games` styled-vs-unstyled matches. Pure and deterministic: same
 * options → same result, bit for bit (the calibration test replays it).
 */
export async function simulateStyledVsUnstyled(opts: SelfplayOptions): Promise<SelfplayResult> {
  const seedBase = opts.seed ?? 1;
  const capPlies = opts.capPlies ?? 120;
  const openingPlies = opts.openingPlies ?? 6;
  let score = 0;
  let draws = 0;
  let divergent = 0;
  let styledPlies = 0;
  const asWhite: { games: number; score: number } = { games: 0, score: 0 };
  const asBlack: { games: number; score: number } = { games: 0, score: 0 };

  for (let i = 0; i < opts.games; i += 1) {
    const out = await playGame(opts.style, seedBase + i, opts.depth, capPlies, openingPlies, opts.windowCp);
    score += out.styledScore;
    if (out.draw) draws += 1;
    divergent += out.divergent;
    styledPlies += out.styledPlies;
    const seat = out.asWhite ? asWhite : asBlack;
    seat.games += 1;
    seat.score += out.styledScore;
  }

  return {
    games: opts.games,
    scorePct: opts.games > 0 ? score / opts.games : 0,
    draws,
    divergent,
    styledPlies,
    bySeat: {
      asWhite: { games: asWhite.games, score: asWhite.games ? asWhite.score / asWhite.games : 0 },
      asBlack: { games: asBlack.games, score: asBlack.games ? asBlack.score / asBlack.games : 0 },
    },
    seedBase,
  };
}
